/**
 * UGC Section JavaScript
 * Handles video popup, carousel, and product interactions
 */

class UGCSection {
  constructor(element) {
    this.element = element;
    this.modal = null;
    this.currentVideo = null;
    this.isVideoPlaying = false;
    this.isVideoMuted = true;
    this.carousel = null;
    this.currentModalIndex = 0;
    this.modalProductCarousels = new Map(); // Track Flickity instances for each products list
    
    this.init();
  }

  init() {
    this.initCarousel();
    this.initVideoPopups();
    this.initModal();
    this.initViewportAutoplay();
  }
  
  querySelector(selector) {
    return this.element.querySelector(selector);
  }
  
  querySelectorAll(selector) {
    return this.element.querySelectorAll(selector);
  }

  initCarousel() {
    const carousel = this.querySelector('.ugc-carousel');
    if (!carousel) return;

    // Check if Flickity is available
    if (typeof Flickity === 'undefined') {
      console.error('Flickity is not loaded. Make sure slideshow-vendor.js is included.');
      return;
    }

    const autoplay = carousel.dataset.autoplay === 'true';
    
    // Initialize Flickity carousel with responsive settings
    const flkty = new Flickity(carousel, {
      cellAlign: 'left',
      contain: true,
      wrapAround: false,
      autoPlay: autoplay ? 5000 : false,
      prevNextButtons: true,
      pageDots: false,
      cellSelector: '.ugc-slide',
      groupCells: false,
      adaptiveHeight: false,
      on: {
        ready: () => {
          // Apply responsive layout
          this.updateCarouselLayout();
          // Replace default arrows with custom SVG
          this.replaceArrowIcons();
        }
      }
    });

    // Store reference for later use
    this.carousel = flkty;
    
    // Update layout on window resize
    window.addEventListener('resize', () => {
      this.updateCarouselLayout();
      this.updateModalProductCarousels();
    });
  }
  
  updateCarouselLayout() {
    const slides = this.querySelectorAll('.ugc-slide');
    const windowWidth = window.innerWidth;
    let slidesPerView = 6;
    
    if (windowWidth <= 580) {
      slidesPerView = 1.9;
    } else if (windowWidth <= 980) {
      slidesPerView = 3;
    } else if (windowWidth <= 1024) {
      slidesPerView = 4;
    } else if (windowWidth <= 1330) {
      slidesPerView = 5;
    } else {
      slidesPerView = 6;
    }
    
    const slideWidth = (100 / slidesPerView) + '%';
    slides.forEach(slide => {
      slide.style.width = slideWidth;
    });
    
    if (this.carousel) {
      this.carousel.resize();
    }
  }

  replaceArrowIcons() {
    const carousel = this.querySelector('.ugc-carousel');
    if (!carousel) return;

    // SVG for prev arrow (carousel-prev)
    const prevSVG = '<svg width="8" height="14" viewBox="0 0 8 14" fill="none"><path d="M7 13L0.999999 7L7 1" stroke="var(--color-body)" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    
    // SVG for next arrow (carousel-next)
    const nextSVG = '<svg width="8" height="14" viewBox="0 0 8 14" fill="none"><path d="M0.849976 13.25L6.84998 7.25L0.849976 1.25" stroke="var(--color-body)" stroke-linecap="round" stroke-linejoin="round"/></svg>';

    // Replace prev button SVG
    const prevButton = carousel.querySelector('.flickity-button.previous');
    if (prevButton) {
      prevButton.innerHTML = prevSVG;
    }

    // Replace next button SVG
    const nextButton = carousel.querySelector('.flickity-button.next');
    if (nextButton) {
      nextButton.innerHTML = nextSVG;
    }
  }

  initVideoPopups() {
    const videoContainers = this.querySelectorAll('.ugc-video-container');
    
    videoContainers.forEach(container => {
      const playButton = container.querySelector('.ugc-play-button');
      const videoUrl = container.dataset.videoUrl;
      const videoType = container.dataset.videoType;
      
      if (playButton && videoUrl) {
        playButton.addEventListener('click', (e) => {
          e.preventDefault();
          this.openVideoModal(videoUrl, videoType, container);
        });
      }
    });
  }

  initModal() {
    this.modal = document.getElementById('UGC-Video-Modal');
    if (!this.modal) return;

    const closeButton = this.modal.querySelector('.ugc-modal__close');
    const prevButton = this.modal.querySelector('.ugc-modal__arrow--prev');
    const nextButton = this.modal.querySelector('.ugc-modal__arrow--next');
    const volumeButton = this.modal.querySelector('.ugc-modal__control--volume');
    const pauseButton = this.modal.querySelector('.ugc-modal__control--pause');
    const video = this.modal.querySelector('.ugc-modal__video');

    // Close modal
    if (closeButton) {
      closeButton.addEventListener('click', () => {
        this.closeVideoModal();
      });
    }

    // Navigation arrows
    if (prevButton) {
      prevButton.addEventListener('click', () => {
        this.navigateModal(-1);
      });
    }

    if (nextButton) {
      nextButton.addEventListener('click', () => {
        this.navigateModal(1);
      });
    }

    // Volume toggle
    if (volumeButton && video) {
      volumeButton.addEventListener('click', () => {
        this.toggleVolume(video, volumeButton);
      });
    }

    // Pause/Play toggle
    if (pauseButton && video) {
      pauseButton.addEventListener('click', () => {
        this.togglePlayPause(video, pauseButton);
      });
    }

    // Close on escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.modal.classList.contains('is-active')) {
        this.closeVideoModal();
      }
      // Arrow key navigation
      if (this.modal.classList.contains('is-active')) {
        if (e.key === 'ArrowLeft') {
          this.navigateModal(-1);
        } else if (e.key === 'ArrowRight') {
          this.navigateModal(1);
        }
      }
    });

    // Close on backdrop click
    this.modal.addEventListener('click', (e) => {
      if (e.target === this.modal) {
        this.closeVideoModal();
      }
    });
  }

  navigateModal(direction) {
    const slides = this.querySelectorAll('.ugc-slide');
    const totalSlides = slides.length;
    
    this.currentModalIndex = this.currentModalIndex + direction;
    
    // Wrap around
    if (this.currentModalIndex < 0) {
      this.currentModalIndex = totalSlides - 1;
    } else if (this.currentModalIndex >= totalSlides) {
      this.currentModalIndex = 0;
    }
    
    // Get the new slide data
    const currentSlide = slides[this.currentModalIndex];
    const videoContainer = currentSlide.querySelector('.ugc-video-container');
    
    if (videoContainer) {
      const videoUrl = videoContainer.dataset.videoUrl;
      const videoType = videoContainer.dataset.videoType;
      
      // Update video
      const video = this.modal.querySelector('.ugc-modal__video');
      const source = video.querySelector('source');
      source.src = videoUrl;
      video.load();
      video.muted = this.isVideoMuted;
      
      // Play video
      setTimeout(() => {
        video.play().catch(error => {
          console.log('Video play failed:', error);
        });
      }, 100);
      
      // Update products display
      this.updateModalProducts(this.currentModalIndex);
    }
  }

  updateModalProducts(index) {
    const productsLists = this.modal.querySelectorAll('.ugc-modal__products-list');
    productsLists.forEach((list, i) => {
      if (i === index) {
        list.classList.add('is-active');
        list.style.display = 'block';
        
        // Initialize Flickity for this products list AFTER it's visible
        setTimeout(() => {
          this.initProductListFlickity(i, list);
          
          // Then resize after initialization
          setTimeout(() => {
            this.resizeProductListFlickity(i, list);
          }, 100);
        }, 50);
      } else {
        list.classList.remove('is-active');
        list.style.display = 'none';
      }
    });
  }

  initProductListFlickity(index, list) {
    // Check if already initialized
    if (this.modalProductCarousels.has(index)) {
      // Just resize the existing instance
      this.resizeProductListFlickity(index, list);
      return;
    }

    const productItems = list.querySelectorAll('.ugc-modal__product-item');
    
    // Only initialize if there are multiple products and element is visible
    if (productItems.length > 1 && list.offsetWidth > 0 && list.offsetHeight > 0) {
      // Wait for Flickity if not available yet
      const initFlickity = () => {
        if (typeof Flickity !== 'undefined') {
          try {
            const flkty = new Flickity(list, {
              cellAlign: 'left',
              contain: true,
              wrapAround: false,
              prevNextButtons: false,
              pageDots: false,
              cellSelector: '.ugc-modal__product-item',
              groupCells: false,
              adaptiveHeight: false,
              dragThreshold: window.innerWidth <= 980 ? 10 : false,
              watchCSS: false,
              on: {
                ready: () => {
                  // Apply slide widths when ready
                  this.updateProductSlideWidths(list);
                }
              }
            });

            this.modalProductCarousels.set(index, flkty);
            
            // Apply slide widths and resize after a short delay
            setTimeout(() => {
              this.updateProductSlideWidths(list);
              if (flkty && typeof flkty.resize === 'function') {
                flkty.resize();
              }
            }, 50);
            
          } catch (error) {
            console.log('Failed to initialize product carousel:', error);
          }
        } else {
          // Retry after a short delay if Flickity isn't loaded yet
          setTimeout(initFlickity, 100);
        }
      };
      
      initFlickity();
    } else if (productItems.length > 1) {
      // Element not visible yet, try again later
      setTimeout(() => {
        this.initProductListFlickity(index, list);
      }, 100);
    }
  }

  resizeProductListFlickity(index, list) {
    const flkty = this.modalProductCarousels.get(index);
    if (flkty && typeof flkty.resize === 'function') {
      // Ensure element is visible before resizing
      const originalDisplay = list.style.display;
      if (originalDisplay === 'none' || list.offsetHeight === 0) {
        // Element is still hidden or has no height, try again later
        setTimeout(() => {
          this.resizeProductListFlickity(index, list);
        }, 50);
        return;
      }
      
      // Update slide widths first
      this.updateProductSlideWidths(list);
      
      // Force Flickity to recalculate its dimensions
      setTimeout(() => {
        try {
          flkty.resize();
          // Double-check the height after resize
          const viewport = list.querySelector('.flickity-viewport');
          if (viewport && viewport.style.height === '0px') {
            // If still 0 height, try once more with a longer delay
            setTimeout(() => {
              flkty.resize();
            }, 100);
          }
        } catch (error) {
          console.log('Error resizing Flickity:', error);
        }
      }, 10);
    }
  }

  updateProductSlideWidths(list) {
    const productItems = list.querySelectorAll('.ugc-modal__product-item');
    productItems.forEach(item => {
      item.style.width = '80%';
      item.style.marginRight = '20px';
    });
    
    // Find and update the Flickity instance
    const flktyInstance = Array.from(this.modalProductCarousels.values())
      .find(flkty => flkty.element === list);
    
    if (flktyInstance) {
      setTimeout(() => {
        flktyInstance.resize();
      }, 10);
    }
  }

  updateModalProductCarousels() {
    // Update all modal product carousels on resize
    this.modalProductCarousels.forEach((flkty) => {
      if (flkty && typeof flkty.resize === 'function') {
        this.updateProductSlideWidths(flkty.element);
        flkty.resize();
      }
    });
  }

  openVideoModal(videoUrl, videoType, sourceContainer) {
    if (!this.modal) return;

    const video = this.modal.querySelector('.ugc-modal__video');
    const source = video.querySelector('source');
    const volumeButton = this.modal.querySelector('.ugc-modal__control--volume');
    const pauseButton = this.modal.querySelector('.ugc-modal__control--pause');

    // Pause all carousel videos when modal opens
    this.pauseAllCarouselVideos();

    // Set video source
    source.src = videoUrl;
    video.load();
    video.muted = true; // Start muted
    this.isVideoMuted = true;
    this.isVideoPlaying = false;

    // Find the slide index
    const slideIndex = Array.from(this.querySelectorAll('.ugc-slide')).indexOf(sourceContainer.closest('.ugc-slide'));
    this.currentModalIndex = slideIndex >= 0 ? slideIndex : 0;

    // Show modal
    this.modal.classList.add('is-active');
    document.body.classList.add('overflow-hidden');

    // Update products display (this will handle Flickity initialization)
    this.updateModalProducts(this.currentModalIndex);

    // Play video after a short delay
    setTimeout(() => {
      video.play().then(() => {
        this.isVideoPlaying = true;
        this.updatePauseButton(pauseButton, true);
      }).catch(error => {
        console.log('Video play failed:', error);
      });
    }, 300);
  }

  closeVideoModal() {
    if (!this.modal) return;

    const video = this.modal.querySelector('.ugc-modal__video');
    
    // Pause and reset video
    if (video) {
      video.pause();
      video.currentTime = 0;
    }

    // Hide modal
    this.modal.classList.remove('is-active');
    document.body.classList.remove('overflow-hidden');

    // Reset states
    this.isVideoPlaying = false;
    this.isVideoMuted = true;

    // Resume autoplay for videos in viewport after modal closes
    setTimeout(() => {
      this.resumeVisibleVideos();
    }, 300);
  }

  toggleVolume(video, button) {
    video.muted = !video.muted;
    this.isVideoMuted = video.muted;
    this.updateVolumeButton(button, this.isVideoMuted);
  }

  togglePlayPause(video, button) {
    if (this.isVideoPlaying) {
      video.pause();
      this.isVideoPlaying = false;
    } else {
      video.play().then(() => {
        this.isVideoPlaying = true;
      }).catch(error => {
        console.log('Video play failed:', error);
      });
    }
    this.updatePauseButton(button, this.isVideoPlaying);
  }

  updateVolumeButton(button, isMuted) {
    if (!button) return;
    
    const svg = button.querySelector('svg');
    if (svg) {
      if (isMuted) {
        button.setAttribute('aria-label', 'Unmute video');
        svg.innerHTML = '<path d="M11 5L6 9H2v6h4l5 4V5z" fill="currentColor"/><line x1="23" y1="9" x2="17" y2="15" stroke="currentColor" stroke-width="2"/><line x1="17" y1="9" x2="23" y2="15" stroke="currentColor" stroke-width="2"/>';
      } else {
        button.setAttribute('aria-label', 'Mute video');
        svg.innerHTML = '<path d="M11 5L6 9H2v6h4l5 4V5z" fill="currentColor"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07" stroke="currentColor" stroke-width="2" fill="none"/>';
      }
    }
  }

  updatePauseButton(button, isPlaying) {
    if (!button) return;
    
    const svg = button.querySelector('svg');
    if (svg) {
      if (isPlaying) {
        button.setAttribute('aria-label', 'Pause video');
        svg.innerHTML = '<rect x="6" y="4" width="4" height="16" rx="1" fill="currentColor"/><rect x="14" y="4" width="4" height="16" rx="1" fill="currentColor"/>';
      } else {
        button.setAttribute('aria-label', 'Play video');
        svg.innerHTML = '<path d="M8 5v14l11-7z" fill="currentColor"/>';
      }
    }
  }

  initViewportAutoplay() {
    // Use Intersection Observer to play/pause videos based on viewport visibility
    const videos = this.querySelectorAll('.ugc-video');
    
    // Options for the observer (trigger when 50% of the video is visible)
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.5
    };
    
    // Create the observer
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const video = entry.target;
        
        if (entry.isIntersecting) {
          // Video is in viewport, play it
          video.play().catch(error => {
            console.log('Video autoplay failed:', error);
          });
        } else {
          // Video is out of viewport, pause it
          video.pause();
        }
      });
    }, observerOptions);
    
    // Observe all videos
    videos.forEach(video => {
      observer.observe(video);
    });
    
    // Store observer for cleanup
    this.videoObserver = observer;
    
    // Pause videos when page visibility changes (tab switching)
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.pauseAllCarouselVideos();
      } else {
        // Resume visible videos when coming back to the tab
        setTimeout(() => {
          this.resumeVisibleVideos();
        }, 100);
      }
    });
    
    // Pause videos when scrolling starts (for better performance)
    let scrollTimeout;
    window.addEventListener('scroll', () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        // Re-trigger the observer after scroll ends
        videos.forEach(video => {
          const rect = video.getBoundingClientRect();
          const isInViewport = rect.top < window.innerHeight && rect.bottom > 0;
          if (!isInViewport) {
            video.pause();
          }
        });
      }, 150);
    }, { passive: true });
  }
  
  pauseAllCarouselVideos() {
    // Pause all carousel videos (not modal videos)
    const videos = this.querySelectorAll('.ugc-video');
    videos.forEach(video => {
      video.pause();
      // Don't reset currentTime to keep videos at their current position
    });
  }

  resumeVisibleVideos() {
    // Play videos that are currently visible in the viewport
    const videos = this.querySelectorAll('.ugc-video');
    videos.forEach(video => {
      const rect = video.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
      
      // Calculate what percentage of the video is visible
      const visibleHeight = Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0);
      const visibleWidth = Math.min(rect.right, viewportWidth) - Math.max(rect.left, 0);
      const videoHeight = rect.height;
      const videoWidth = rect.width;
      
      // Check if at least 50% of the video is visible
      const isVisible = (visibleHeight / videoHeight) >= 0.5 && 
                        (visibleWidth / videoWidth) >= 0.5 &&
                        rect.top < viewportHeight && 
                        rect.bottom > 0;
      
      if (isVisible) {
        video.play().catch(error => {
          console.log('Video play failed:', error);
        });
      }
    });
  }

  playCurrentVideo(index) {
    const slides = this.querySelectorAll('.ugc-slide');
    if (slides[index]) {
      const video = slides[index].querySelector('.ugc-video');
      if (video) {
        video.play().catch(error => {
          console.log('Video autoplay failed:', error);
        });
      }
    }
  }


  destroy() {
    // Clean up event listeners and destroy carousel
    if (this.carousel) {
      this.carousel.destroy();
    }
    
    // Destroy modal product carousels
    this.modalProductCarousels.forEach((flkty) => {
      if (flkty && typeof flkty.destroy === 'function') {
        flkty.destroy();
      }
    });
    this.modalProductCarousels.clear();
    
    // Disconnect video observer
    if (this.videoObserver) {
      this.videoObserver.disconnect();
    }
  }
}

// Initialize UGC sections when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initUGCSections);
} else {
  initUGCSections();
}

function initUGCSections() {
  const ugcSections = document.querySelectorAll('.ugc-section');
  ugcSections.forEach(section => {
    // Check if already initialized
    if (!section.dataset.ugcInitialized) {
      new UGCSection(section);
      section.dataset.ugcInitialized = 'true';
    }
  });
}

// Handle Shopify section loading in theme editor
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const ugcSection = event.target.querySelector('.ugc-section');
    if (ugcSection && !ugcSection.dataset.ugcInitialized) {
      new UGCSection(ugcSection);
      ugcSection.dataset.ugcInitialized = 'true';
    }
  });
}
